/**
 * Alarm/event views fronting the nine per-category alarm sources plus the
 * cross-workflow `WfAlarms_Agent` superset.
 *
 * Every alarm source stores its columns VERBATIM with NO prefix — each column
 * is already its bare canonical name (`alarmName`, `workflowCode`, …) — so this
 * is a pure de-prefix/projection reshape: the shared shapes are authored with
 * `suffix === canonical` and applied with the default empty prefix, emitting
 * only the `(columns …)` projection and no `(rename …)` ops, exactly like
 * `dailyRollupViews` / `networkInputsViews`.
 *
 * The sources are NOT byte-identical, so ONE shared shape does not suffice; the
 * spec table drives three composed extras over a single `ALARM_EVENT` base
 * (confirmed against the PARSED view columns, not the zod schema — the
 * generated views' `.omit({startDateTime,endDateTime})` only fires on some
 * sources):
 *   - `hasCounterTicket` — `ESPAlarms_Agent`, `GasLiftAlarms_Agent`,
 *     `InjectionAlarms_Agent`, `NetworkAlarms_Agent`,
 *     `ProductionAllocationAlarms_Agent` (and the `WfAlarms_Agent` superset)
 *     carry `alarmCounter` + `ticketId`; the other four do not, so authoring
 *     those suffixes against them would silently `unknownCol` and break the
 *     no-unknownCol guard.
 *   - `hasTimestamps` — `startDateTime` + `endDateTime` survive on every source
 *     EXCEPT `ESPAlarms_Agent` and `WfAlarms_Agent`, whose generated views drop
 *     them via `.omit({startDateTime,endDateTime})`. Composed only where they
 *     resolve.
 *   - `AllAlarms` (the `WfAlarms_Agent` superset) additionally keeps
 *     `alarmCategory` + the Wf-only `alarmMessage`; the nine per-category views
 *     DROP the redundant `alarmCategory` (the category is implied by the view
 *     name).
 *
 * `keep` for every view is identity + the entity-identity scalars
 * (`entityName`/`entityExternalId`/`entityType`) + the `wellString`
 * direct-relation — the grain refs the agent scopes by / joins (it joins the
 * *Entity views by ref; these views do NOT name-enrich).
 *
 * These are `createReshapedView` (rewrite) views — pure de-prefix/projection
 * over the source schema, owning no fetch — so they need no `deps`; they are a
 * module-level factory spread into `createAppVirtualViews`, mirroring
 * `dailyRollupViews` / `networkInputsViews`.
 */

import type { DataModelView } from '#core/lib/cognite/view.ts';

import { createReshapedView, type VirtualView } from '../createReshapedView.ts';
import {
  ALARM_COUNTER_TICKET,
  ALARM_ENTITY_RELATION,
  ALARM_EVENT,
  ALARM_SUPERSET_EXTRAS,
  ALARM_TIMESTAMPS,
  IDENTITY,
  type Shape,
  STRING_RELATION,
} from '../shapes/index.ts';
import { useCatalogEnrichContext } from './useCatalogEnrichContext.ts';
import {
  EspAlarmsAgentView,
  FlowAssuranceAlarmsAgentView,
  GasLiftAlarmsAgentView,
  InjectionAlarmsAgentView,
  ModelManagementAlarmsAgentView,
  NetworkAlarmsAgentView,
  ProductionAllocationAlarmsAgentView,
  SurveillanceAlarmsAgentView,
  WellBehaviourAlarmsAgentView,
  WfAlarmsAgentView,
} from '#schema/generated/ipso/views.ts';

/**
 * Identity + the entity-identity scalars + the producing-string direct-relation
 * kept unrenamed on every alarm view — the grain refs the agent scopes by /
 * joins. `entityName`/`entityExternalId`/`entityType` are scalar identity (the
 * source models the alarmed entity as three columns, not one relation);
 * `wellString` is the lone direct-relation.
 */
const ALARM_KEEP: Shape = [...IDENTITY, ...ALARM_ENTITY_RELATION, ...STRING_RELATION];

/** One alarm surface: its registry name, source view, agent doc, and the composed extras it carries. */
type AlarmSpec = {
  /** Agent-facing registry name. */
  readonly name: string;
  /** View-level description shown to the agent. */
  readonly description: string;
  /** The source CDF view this reshapes. */
  readonly source: DataModelView;
  /** Whether the source exposes `alarmCounter` + `ticketId`. */
  readonly hasCounterTicket: boolean;
  /** Whether the source's parsed view exposes `startDateTime` + `endDateTime`. */
  readonly hasTimestamps: boolean;
  /** Superset-only extras (`alarmCategory` + `alarmMessage`) — kept only by `AllAlarms`. */
  readonly supersetExtras: boolean;
};

/**
 * A per-category alarm surface. The nine domain sources share the same axis
 * (one source per alarm category, category implied by the view name) and DROP
 * the redundant `alarmCategory` column; `hasTimestamps` and `hasCounterTicket`
 * vary per source (see the module doc). All carry `wellString` + the entity
 * scalars.
 */
const domain = (
  name: string,
  source: DataModelView,
  category: string,
  hasCounterTicket: boolean,
  hasTimestamps: boolean,
): AlarmSpec => ({
  name,
  description: `${category} alarm/event rows: alarm code, name, priority, criticality, flags, workflow context${
    hasTimestamps ? ', and the trigger/clear timestamps' : ''
  }${
    hasCounterTicket ? ', plus the occurrence counter and troubleshooting ticket id' : ''
  }. One row per ${category} alarm occurrence. Filter by wellString / the entity scalars (or join the well entity) to scope to a producing string.`,
  source,
  hasCounterTicket,
  hasTimestamps,
  supersetExtras: false,
});

/**
 * The nine per-category alarm surfaces plus the cross-workflow `AllAlarms`
 * superset, declared as data. Each de-prefixes the shared `ALARM_EVENT` base
 * (plus the timestamp / counter-ticket extras the source actually exposes) over
 * one alarm category, keeping identity + the entity scalars + `wellString`.
 * `AllAlarms` fronts `WfAlarms_Agent`, keeping `alarmCategory` + the Wf-only
 * `alarmMessage` and the counter/ticket extras, but no timestamps.
 */
const ALARM_SPECS: readonly AlarmSpec[] = [
  domain('EspAlarms', EspAlarmsAgentView, 'ESP/motor (WF12)', true, false),
  domain('GasLiftAlarms', GasLiftAlarmsAgentView, 'Gas-lift (WF14)', true, true),
  domain('InjectionAlarms', InjectionAlarmsAgentView, 'Injection', true, true),
  domain('NetworkAlarms', NetworkAlarmsAgentView, 'Network', true, true),
  domain('ProductionAllocationAlarms', ProductionAllocationAlarmsAgentView, 'Production-allocation', true, true),
  domain('FlowAssuranceAlarms', FlowAssuranceAlarmsAgentView, 'Flow-assurance', false, true),
  domain('ModelManagementAlarms', ModelManagementAlarmsAgentView, 'Model-management', false, true),
  domain('SurveillanceAlarms', SurveillanceAlarmsAgentView, 'Surveillance', false, true),
  domain('WellBehaviourAlarms', WellBehaviourAlarmsAgentView, 'Well-behaviour', false, true),
  {
    name: 'AllAlarms',
    description:
      'Cross-workflow denormalized alarm/event rows from the WfAlarms superset (~48h retention): alarm code, name, message, priority, criticality, flags, the alarm category and full workflow context, plus the occurrence counter and troubleshooting ticket id. One row per alarm occurrence across every category. Filter by alarmCategory to scope to a domain, or wellString / the entity scalars (or join the well entity) to scope to a producing string.',
    source: WfAlarmsAgentView,
    hasCounterTicket: true,
    hasTimestamps: false,
    supersetExtras: true,
  },
];

/** The measure groups a spec projects: the shared base plus only the extras the source exposes. */
const groupsFor = (spec: AlarmSpec): readonly Shape[] => [
  ALARM_EVENT,
  ...(spec.hasTimestamps ? [ALARM_TIMESTAMPS] : []),
  ...(spec.hasCounterTicket ? [ALARM_COUNTER_TICKET] : []),
  ...(spec.supersetExtras ? [ALARM_SUPERSET_EXTRAS] : []),
];

/**
 * Build the ten de-prefixed alarm surfaces (nine per-category + the `AllAlarms`
 * superset) fronting their respective sources. Pure reshape views — no `deps`
 * needed; each projects the shared `ALARM_EVENT` measures (plus the extras the
 * source exposes) over its category, keeping identity + the entity scalars +
 * `wellString`.
 */
export const useAlarmsViews = (): readonly VirtualView[] => {
  const { client, mintSeries } = useCatalogEnrichContext();
  return ALARM_SPECS.map((spec: AlarmSpec) =>
    createReshapedView({
      name: spec.name,
      description: spec.description,
      source: spec.source,
      keep: ALARM_KEEP,
      groups: groupsFor(spec).map((shape: Shape) => ({ shape })),
      client,
      mintSeries,
    }),
  );
};
